import { create } from "zustand";

import { IHttpError } from "models/interface";
import { pageSizeConfigs } from "config/commonConfig";
import { RankingProjectService } from "../../../services/rankingProject.service";
import {
  IMedalWinningQuestion,
  IRankingSnapshotHeader,
  IRankingSnapshotRow,
} from "../../../models/interface";

export type SnapshotSortColumn =
  | "record"
  | "effective"
  | "through"
  | "series"
  | "rank"
  | "points";
export type SnapshotSortOrder = "asc" | "desc";

interface RankingSnapshotState {
  isLoading: boolean;
  dataFetchError: IHttpError | null;
  header: IRankingSnapshotHeader | null;
  rows: IRankingSnapshotRow[];
  count: number;
  actualCount: number;
  pageIndex: number;
  pageSize: number;
  sortColumn: SnapshotSortColumn | null;
  sortOrder: SnapshotSortOrder | null;
  searchTerm: string;
  selectedRanks: string[];
  selectedScores: number[];
  currentRankingProjectId: number | null;
  latestRequestId: number;
  selectedRecord: IRankingSnapshotRow | null;
  isDrawerOpen: boolean;
  medalQuestions: IMedalWinningQuestion[];
  medalQuestionsLoading: boolean;
  medalQuestionsRefreshing: boolean;
  latestMedalQuestionsRequestId: number;
  medalQuestionsCache: Map<number, IMedalWinningQuestion[]>;
  openRecordDrawer: (
    project_slug: string,
    ranking_project_id: number,
    record: IRankingSnapshotRow,
  ) => Promise<void>;
  closeRecordDrawer: () => void;
  loadRankingSnapshot: (
    project_slug: string,
    ranking_project_id: number,
    initialRankFilter?: string[],
    initialScoreFilter?: number[],
  ) => Promise<void>;
  fetchRankingSnapshot: (
    project_slug: string,
    ranking_project_id: number,
  ) => Promise<void>;
  setSort: (
    project_slug: string,
    ranking_project_id: number,
    sortColumn: SnapshotSortColumn | null,
    sortOrder: SnapshotSortOrder | null,
  ) => void;
  setSearch: (
    project_slug: string,
    ranking_project_id: number,
    searchTerm: string,
  ) => void;
  toggleRankFilter: (
    project_slug: string,
    ranking_project_id: number,
    rankName: string,
  ) => void;
  toggleScoreFilter: (
    project_slug: string,
    ranking_project_id: number,
    score: number,
  ) => void;
  setPage: (
    project_slug: string,
    ranking_project_id: number,
    pageIndex: number,
  ) => void;
  setPageSize: (
    project_slug: string,
    ranking_project_id: number,
    pageSize: number,
  ) => void;
}

export const useRankingSnapshotStore = create<RankingSnapshotState>(
  (set, get) => ({
    isLoading: true,
    dataFetchError: null,
    header: null,
    rows: [],
    count: 0,
    actualCount: 0,
    pageIndex: 1,
    pageSize: pageSizeConfigs.DEFAULT_PAGE_SIZE,
    sortColumn: null,
    sortOrder: null,
    searchTerm: "",
    selectedRanks: [],
    selectedScores: [],
    currentRankingProjectId: null,
    latestRequestId: 0,
    selectedRecord: null,
    isDrawerOpen: false,
    medalQuestions: [],
    medalQuestionsLoading: false,
    medalQuestionsRefreshing: false,
    latestMedalQuestionsRequestId: 0,
    medalQuestionsCache: new Map(),

    openRecordDrawer: async (project_slug, ranking_project_id, record) => {
      const requestId = get().latestMedalQuestionsRequestId + 1;
      const cached = get().medalQuestionsCache.get(record.record_id);
      set(() => ({
        selectedRecord: record,
        isDrawerOpen: true,
        medalQuestions: cached ?? [],
        medalQuestionsLoading: cached === undefined,
        medalQuestionsRefreshing: cached !== undefined,
        latestMedalQuestionsRequestId: requestId,
      }));
      try {
        const { data } =
          await new RankingProjectService().getRankingSnapshotRecordQuestions(
            project_slug,
            ranking_project_id,
            record.record_id,
          );
        if (get().latestMedalQuestionsRequestId !== requestId) {
          return;
        }
        const questions = data.data.medal_winning_questions ?? [];
        set((state) => ({
          medalQuestions: questions,
          medalQuestionsLoading: false,
          medalQuestionsRefreshing: false,
          medalQuestionsCache: new Map(state.medalQuestionsCache).set(
            record.record_id,
            questions,
          ),
        }));
      } catch (error: any) {
        if (get().latestMedalQuestionsRequestId !== requestId) {
          return;
        }
        set(() => ({
          medalQuestions: cached ?? [],
          medalQuestionsLoading: false,
          medalQuestionsRefreshing: false,
        }));
      }
    },

    closeRecordDrawer: () => {
      set(() => ({
        isDrawerOpen: false,
        selectedRecord: null,
        medalQuestions: [],
        medalQuestionsLoading: false,
        medalQuestionsRefreshing: false,
      }));
    },

    loadRankingSnapshot: async (
      project_slug: string,
      ranking_project_id: number,
      initialRankFilter?: string[],
      initialScoreFilter?: number[],
    ) => {
      if (get().currentRankingProjectId !== ranking_project_id) {
        set(() => ({
          currentRankingProjectId: ranking_project_id,
          header: null,
          rows: [],
          count: 0,
          actualCount: 0,
        }));
      }
      set(() => ({
        pageIndex: 1,
        sortColumn: null,
        sortOrder: null,
        searchTerm: "",
        selectedRanks: initialRankFilter ?? [],
        selectedScores: initialScoreFilter ?? [],
      }));
      await get().fetchRankingSnapshot(project_slug, ranking_project_id);
    },

    fetchRankingSnapshot: async (
      project_slug: string,
      ranking_project_id: number,
    ) => {
      const {
        pageIndex,
        pageSize,
        sortColumn,
        sortOrder,
        searchTerm,
        selectedRanks,
        selectedScores,
      } = get();
      const requestId = get().latestRequestId + 1;
      set(() => ({ isLoading: true, latestRequestId: requestId }));
      try {
        const { data } = await new RankingProjectService().getRankingSnapshot(
          project_slug,
          ranking_project_id,
          {
            page_index: pageIndex,
            page_size: pageSize,
            sort_column: sortColumn,
            sort_order: sortOrder,
            search: searchTerm,
            ...(selectedRanks.length > 0 ? { rank_filter: selectedRanks } : {}),
            ...(selectedScores.length > 0
              ? { score_filter: selectedScores }
              : {}),
          },
        );
        if (get().latestRequestId !== requestId) {
          return;
        }
        set(() => ({
          header: data.data.header,
          rows: data.data.rows ?? [],
          count: data.data.count ?? 0,
          actualCount: data.data.actual_count ?? 0,
          isLoading: false,
          dataFetchError: null,
        }));
      } catch (error: any) {
        if (get().latestRequestId !== requestId) {
          return;
        }
        set(() => ({ dataFetchError: error, isLoading: false }));
      }
    },

    setSort: (project_slug, ranking_project_id, sortColumn, sortOrder) => {
      set(() => ({ sortColumn, sortOrder, pageIndex: 1 }));
      get().fetchRankingSnapshot(project_slug, ranking_project_id);
    },

    setSearch: (project_slug, ranking_project_id, searchTerm) => {
      set(() => ({ searchTerm, pageIndex: 1 }));
      get().fetchRankingSnapshot(project_slug, ranking_project_id);
    },

    toggleRankFilter: (project_slug, ranking_project_id, rankName) => {
      set((state) => ({
        selectedRanks: state.selectedRanks.includes(rankName)
          ? state.selectedRanks.filter((name) => name !== rankName)
          : [...state.selectedRanks, rankName],
        pageIndex: 1,
      }));
      get().fetchRankingSnapshot(project_slug, ranking_project_id);
    },

    toggleScoreFilter: (project_slug, ranking_project_id, score) => {
      set((state) => ({
        selectedScores: state.selectedScores.includes(score)
          ? state.selectedScores.filter((value) => value !== score)
          : [...state.selectedScores, score],
        pageIndex: 1,
      }));
      get().fetchRankingSnapshot(project_slug, ranking_project_id);
    },

    setPage: (project_slug, ranking_project_id, pageIndex) => {
      set(() => ({ pageIndex }));
      get().fetchRankingSnapshot(project_slug, ranking_project_id);
    },

    setPageSize: (project_slug, ranking_project_id, pageSize) => {
      set(() => ({ pageSize, pageIndex: 1 }));
      get().fetchRankingSnapshot(project_slug, ranking_project_id);
    },
  }),
);
